import { Media } from '@/components/Media'
import { Link, useParams } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { formatMoney, fromMajor } from '@/lib/money'
import { findVenue, WELLNESS_CASHBACK } from './wellness-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

export function WellnessVenueScreen() {
  const { id = '' } = useParams()
  const venue = findVenue(id)
  if (!venue) return <NotFoundScreen />

  const headline = venue.services[0]
  const price = fromMajor(headline?.price ?? 0, 'USD')
  const cashback = fromMajor((headline?.price ?? 0) * WELLNESS_CASHBACK, 'USD')
  const backTo = venue.kind === 'gym' ? '/gym' : '/wellness'

  return (
    <>
      <PageHeader
        title={venue.name}
        subtitle={`${venue.city} · ${venue.detail}`}
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="arrow_back" to={backTo}>
              {venue.kind === 'gym' ? 'All gyms' : 'All of Wellness'}
            </Button>
            <Button variant="ghost" size="md" iconStart="call">
              Call
            </Button>
          </>
        }
      />

      <div className={checkout.grid}>
        <Stack gap={3}>
          <Media
            src={venue.image}
            alt={`${venue.name}, ${venue.city}`}
            ratio={16 / 9}
            radius="lg"
            icon={venue.kind === 'gym' ? 'exercise' : 'health_and_safety'}
            eager
          />

          <Card tone="light">
            <Stack direction="row" align="center" gap={3} wrap>
              <Pill tone="accent">
                {venue.score} · {venue.verdict}
              </Pill>
              <Text variant="caption" tone="secondary">
                {venue.reviews} reviews · {venue.languages}
              </Text>
            </Stack>
            <Text variant="body" tone="secondary" className="pb-mt-4">
              {venue.blurb}
            </Text>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              {venue.kind === 'gym' ? 'Passes' : 'What they do'}
            </Text>
            <Stack gap={0} className="pb-mt-3">
              {venue.services.map((service) => (
                <Link
                  key={service.name}
                  to={`/wellness/${venue.slug}/book?service=${encodeURIComponent(service.name)}`}
                  className={checkout.line}
                  style={{ width: '100%' }}
                >
                  <Stack grow gap={0} align="start">
                    <Text variant="caption" weight="semibold">
                      {service.name}
                    </Text>
                    <Text variant="caption" tone="secondary">
                      {service.duration}
                      {service.usPrice ? ` · $${service.usPrice.toLocaleString('en-US')} in the US` : ''}
                    </Text>
                  </Stack>
                  <Text variant="caption" weight="semibold" numeric>
                    {service.price === 0 ? 'Free' : formatMoney(fromMajor(service.price, 'USD'))}
                  </Text>
                </Link>
              ))}
            </Stack>
          </Card>
        </Stack>

        <Stack gap={3} className={checkout.summary}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">
                {headline?.name}
              </Text>
              <Pill tone="accent">5% back</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-3">
              {formatMoney(price)}
            </Text>
            <div className={checkout.rule} />
            {[
              [venue.slots[0] ?? 'Next slot', 'Available'],
              ['Paid from', 'Wellness budget · $114 left'],
              ['Cashback', `+${formatMoney(cashback)}`],
              ['Free cancellation', 'Up to 4 hours before'],
            ].map(([label, value], index) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text
                  variant="caption"
                  weight="semibold"
                  tone={index === 2 ? 'accent' : 'onDark'}
                  numeric
                >
                  {value}
                </Text>
              </div>
            ))}
            <Card.Footer>
              <Button size="lg" block iconEnd="arrow_forward" to={`/wellness/${venue.slug}/book`}>
                {venue.kind === 'gym' ? 'Get a pass' : 'Book an appointment'}
              </Button>
              <Stack direction="row" align="center" gap={2} className="pb-mt-3">
                <Icon name="translate" size={15} />
                <Text variant="eyebrow" tone="onDarkMuted">
                  They speak {venue.languages}
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </Stack>
      </div>
    </>
  )
}
